import * as React from 'react'
import { Link } from 'react-router'
import { Loader2, TriangleAlert, X } from 'lucide-react'
import { formatMoney } from '@lib/helpers/money'
import { formatMinutesShort } from '@lib/helpers/minutes'
import { formatTaxRatePercent } from '@lib/helpers/tax'
import { useSwitchToNewestVisits } from '@/hooks/useProposalMutations'
import { toErrorMessage } from '@/helpers/logger'
import { formatVisitDay } from '@/site-visits/VisitBar'
import { Button } from '@/components/ui/button'
import { SELECT_CLASS } from '@/components/ui/field-classes'
import { cn } from '@/lib/utils'
import type { BidContext, BidWork } from './useBid'

/*
 * The parts of the bid tray both survey screens share. Each screen totals
 * only its own kind of work (the operator's call, 2026-11-04), so the trays
 * differ in their rows and agree on everything around them.
 */

export interface BidTrayFrameProps {
  id: string
  title: string
  /** A sheet over the top of the map, or a column beside it. */
  variant: 'overlay' | 'column'
  onClose: () => void
  children: React.ReactNode
  className?: string | undefined
}

/**
 * The tray's frame.
 *
 * On a phone it drops down from the header over the top of the map, so it
 * never meets the editor sheet at the bottom. On a wide screen it is a column
 * on the left, opposite the editor, so both stay open while a tree is edited.
 */
export function BidTrayFrame({
  id,
  title,
  variant,
  onClose,
  children,
  className,
}: BidTrayFrameProps) {
  return (
    <section
      id={id}
      aria-label={title}
      className={cn(
        'flex flex-col bg-surface',
        variant === 'overlay'
          ? 'max-h-[70%] border-b border-default shadow-menu'
          : 'h-full border-r border-default',
        className,
      )}
    >
      <div className="flex h-10 shrink-0 items-center justify-between gap-2 border-b border-default px-3">
        <h2 className="truncate text-sm font-semibold">{title}</h2>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          aria-label="Close the bid total"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {children}
      </div>
    </section>
  )
}

/** A heading inside the tray. */
export function TraySection({
  title,
  aside,
  children,
}: {
  title: string
  aside?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="border-b border-default px-3 py-2">
      <div className="mb-1 flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-medium text-secondary-text">{title}</h3>
        {aside}
      </div>
      {children}
    </div>
  )
}

export interface TrayRowProps {
  label: React.ReactNode
  /** Counts and time, quiet, after the label. */
  detail?: string | null
  cents: number | null
  /** Ends the figure in "+": part of it is not known yet. */
  incomplete?: boolean
  strong?: boolean
  muted?: boolean
}

/** One line: what it is, how much of it, and what it comes to. */
export function TrayRow({
  label,
  detail,
  cents,
  incomplete = false,
  strong = false,
  muted = false,
}: TrayRowProps) {
  return (
    <div
      className={cn(
        'flex items-baseline gap-2 py-0.5 text-sm',
        muted && 'text-secondary-text',
        strong && 'font-semibold',
      )}
    >
      <span className="min-w-0 flex-1">
        <span>{label}</span>
        {detail === null || detail === undefined ? null : (
          <span className="text-xs font-normal text-muted-text">
            {' · '}
            {detail}
          </span>
        )}
      </span>
      <span
        className={cn(
          'shrink-0 tabular-nums',
          incomplete && 'text-warning',
        )}
      >
        {cents === null ? '—' : formatMoney(cents)}
        {incomplete ? '+' : null}
      </span>
    </div>
  )
}

/** "64 trees · 31h 30m". */
export function countAndTime(
  count: number,
  noun: string,
  minutes: number | null,
): string {
  const things = `${String(count)} ${count === 1 ? noun : `${noun}s`}`
  const time = minutes === null || minutes === 0 ? null : formatMinutesShort(minutes)
  return time === null ? things : `${things} · ${time}`
}

/** A line saying something is missing, in amber, with what to do about it. */
export function AttentionLine({
  children,
  action,
}: {
  children: React.ReactNode
  action?: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-1.5 py-0.5 text-sm">
      <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
      <span className="min-w-0 flex-1">{children}</span>
      {action}
    </div>
  )
}

/**
 * Which bid the tray is adding up, and the ways that can be wrong.
 *
 * Names the draft and lets the technician pick another when the site is in
 * more than one. When the draft is still built from an earlier visit, the
 * figures here (today's) are not what it shows yet, so it says so and offers
 * to move the draft onto the newest visit, as the proposal page does.
 */
export function BidSource({
  context,
  work,
  onChooseProposal,
}: {
  context: BidContext
  work: BidWork
  onChooseProposal: (proposalId: string) => void
}) {
  const switchVisits = useSwitchToNewestVisits()
  const [error, setError] = React.useState<string | null>(null)
  const { bid } = context

  if (context.isResolving) {
    return (
      <p className="flex items-center gap-1.5 border-b border-default px-3 py-2 text-xs text-muted-text">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Finding the bid this property is in…
      </p>
    )
  }

  if (bid === null) {
    return (
      <p className="border-b border-default px-3 py-2 text-xs text-secondary-text">
        {context.isUnavailable
          ? 'Proposals can’t be reached without a signal, so this is the property on its own.'
          : 'No draft proposal includes this property yet, so this is the property on its own.'}
      </p>
    )
  }

  const builtFrom = work === 'trees' ? bid.site.treeVisit : bid.site.irrigationVisit
  const newer =
    work === 'trees' ? bid.site.newerTreeVisit : bid.site.newerIrrigationVisit
  const isBehind = builtFrom !== null && newer !== null

  async function handleSwitchVisits(): Promise<void> {
    if (bid === null) return
    setError(null)
    try {
      await switchVisits.mutateAsync({
        versionId: bid.version.id,
        proposalId: bid.proposal.id,
      })
    } catch (caught) {
      setError(toErrorMessage(caught))
    }
  }

  return (
    <div className="space-y-1.5 border-b border-default px-3 py-2">
      <div className="flex items-baseline gap-2 text-xs">
        <span className="min-w-0 flex-1 truncate text-secondary-text">
          <Link
            to={`/proposals/${bid.proposal.id}`}
            className="font-medium text-primary-text underline-offset-2 hover:underline"
          >
            {bid.proposal.title}
          </Link>
          {` · draft v${String(bid.version.versionNumber)}`}
          {bid.version.sites.length > 1
            ? ` · ${String(bid.version.sites.length)} properties`
            : null}
        </span>
      </div>
      {context.drafts.length > 1 ? (
        <label className="block text-xs text-muted-text">
          <span className="sr-only">Which draft to add up</span>
          <select
            className={cn(SELECT_CLASS, 'h-8 text-xs')}
            value={bid.proposal.id}
            onChange={(event) => {
              onChooseProposal(event.target.value)
            }}
          >
            {context.drafts.map((proposal) => (
              <option key={proposal.id} value={proposal.id}>
                {proposal.title}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {isBehind ? (
        <div className="space-y-1 rounded-md bg-warning-subtle px-2 py-1.5 text-xs">
          <p>
            This draft still uses the visit of{' '}
            {formatVisitDay(builtFrom.startedOn)}, so the changes here aren’t
            in it yet. The figures below are the newest visit’s (
            {formatVisitDay(newer.startedOn)}).
          </p>
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            disabled={switchVisits.isPending}
            onClick={() => {
              void handleSwitchVisits()
            }}
          >
            {switchVisits.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            Use the newest visit
          </Button>
          {error === null ? null : (
            <p className="text-destructive">{error}</p>
          )}
        </div>
      ) : null}
    </div>
  )
}

/** The bid's other properties, each as its draft prices it. */
export function OtherProperties({
  sites,
  state,
  mapPath,
  proposalId,
}: {
  sites: readonly {
    siteId: string | null
    name: string
    cents: number
    incomplete: boolean
  }[]
  state: 'loading' | 'unavailable' | 'ready'
  /** The survey screen to open for another site: "survey" or "irrigation". */
  mapPath: 'survey' | 'irrigation'
  proposalId: string
}) {
  if (state === 'loading') {
    return (
      <TraySection title="Other properties">
        <p className="flex items-center gap-1.5 text-xs text-muted-text">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading the rest of the bid…
        </p>
      </TraySection>
    )
  }
  if (state === 'unavailable') {
    return (
      <TraySection title="Other properties">
        <p className="text-xs text-secondary-text">
          The rest of the bid can’t be reached without a signal, so the total is
          this property alone.
        </p>
      </TraySection>
    )
  }
  if (sites.length === 0) return null
  return (
    <TraySection title="Other properties">
      {sites.map((entry) => (
        <TrayRow
          key={entry.siteId ?? entry.name}
          label={
            entry.siteId === null ? (
              entry.name
            ) : (
              <Link
                to={`/sites/${entry.siteId}/${mapPath}?proposal=${proposalId}`}
                className="underline-offset-2 hover:underline"
              >
                {entry.name}
              </Link>
            )
          }
          cents={entry.cents}
          incomplete={entry.incomplete}
        />
      ))}
    </TraySection>
  )
}

/** Before tax, tax, and the total, at the foot of the money. */
export function TaxLines({
  subtotalCents,
  taxRatePercent,
  taxCents,
  totalCents,
  isIncomplete,
}: {
  subtotalCents: number | null
  taxRatePercent: string | null
  taxCents: number | null
  totalCents: number | null
  isIncomplete: boolean
}) {
  return (
    <div className="border-b border-default px-3 py-2">
      <TrayRow label="Before tax" cents={subtotalCents} muted />
      {taxCents === null ? (
        <TrayRow
          label="Tax"
          detail={subtotalCents === null ? null : 'none set'}
          cents={subtotalCents === null ? null : 0}
          muted
        />
      ) : (
        <TrayRow
          label="Tax"
          detail={
            taxRatePercent === null
              ? null
              : `${formatTaxRatePercent(taxRatePercent) ?? taxRatePercent}%`
          }
          cents={taxCents}
          muted
        />
      )}
      <TrayRow
        label="Total"
        cents={totalCents}
        incomplete={isIncomplete}
        strong
      />
    </div>
  )
}
